import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../../lib/sectionFallbacks/derivedFallbacks';
import {
  EditorialSubsection,
  EditorialRecommendations,
  EditorialInstrumentPanel,
  EditorialHeroMetric,
  EditorialStatusChip,
  type EditorialChipStatus,
} from '../../report/editorial';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { makeRenderCitedText, resolveBlockScale, emptyStateNoteProps } from './feasibilityBodyHelpers';
import { isEditorialLayoutsEnabled } from '../../../../lib/runtimeFlags';
import { formatFixed } from '../../../../lib/coerceNumber';

/** Fiber-density → RAG verdict chip (re-skinned to the active preset's
 *  positive/warning/negative roles, NOT a baked color). */
const FIBER_RAG: Record<'High' | 'Medium' | 'Low', EditorialChipStatus> = {
  High: 'green',
  Medium: 'amber',
  Low: 'red',
};

/**
 * #34 P2 STAGE B — Connectivity & Fiber (feasibility). Lifted VERBATIM from the
 * SiteRagReportView inline block (body only; router shell owns the chrome). The
 * inline block did NOT render a ProvenanceStrip; the router strip is null for
 * feasibility, so the DOM is byte-identical.
 */
export const ConnectivityStudySection: React.FC<{ report: SiteRagReport; label?: string }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const renderCitedText = makeRenderCitedText(report);
  const blockScale = resolveBlockScale(theme.headingScale);

  // K3 — connectivity instrument readout. The redesign's dark "NEAREST IX
  // 204.9 km" card, re-skinned to OUR theme tokens. Built from the
  // deterministic infrastructure dataset (latency-to-IX + fiber density), so
  // it only renders when the data is present (never a fabricated number).
  const infra = report.datasets?.infrastructure;
  const latencyMs =
    infra && typeof infra.latency === 'number' && Number.isFinite(infra.latency) && infra.latency > 0
      ? infra.latency
      : null;
  const fiberDensity = infra?.fiberDensity;
  // Latency RAG: ≤5 ms green, ≤15 ms amber, else red (datacenter-grade bands).
  const latencyRag: EditorialChipStatus | null =
    latencyMs == null ? null : latencyMs <= 5 ? 'green' : latencyMs <= 15 ? 'amber' : 'red';

  return !report.connectivityStudy ? (
    // N1.5C-flat — graceful-omit note flattens to a muted left-rule note on the
    // cream page in editorial mode (shared seam); flag OFF byte-identical.
    <div {...emptyStateNoteProps(isEditorialLayoutsEnabled(), theme, true)} data-testid="connectivityStudy-empty">
      {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
    </div>
  ) : (
    // N1 — in the editorial monograph, make the 2-col grid atomic (gated) so it
    // never splits mid-row across a page (a fragmented CSS grid desynced its
    // column tracks → overlap). The grid spans both N2 flow columns via
    // `data-span-columns`; flag-OFF is byte-stable.
    <div data-span-columns style={isEditorialLayoutsEnabled() ? { breakInside: 'avoid' } : undefined} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
      {/* Assessment sub-blocks → theme-adaptive EditorialSubsection (no boxy card). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: blockScale === 'compact' ? 10 : 16 }}>
        <EditorialSubsection theme={theme} label="Fiber Infrastructure">{renderCitedText(report.connectivityStudy.fiberInfrastructure)}</EditorialSubsection>
        <EditorialSubsection theme={theme} label="Internet Exchange">{renderCitedText(report.connectivityStudy.internetExchange)}</EditorialSubsection>
        <EditorialSubsection theme={theme} label="Data Center Facilities">{renderCitedText(report.connectivityStudy.datacenters)}</EditorialSubsection>
        <EditorialSubsection theme={theme} label="Latency Analysis">{renderCitedText(report.connectivityStudy.latencyAnalysis)}</EditorialSubsection>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {latencyMs != null && (
          <EditorialInstrumentPanel
            theme={theme}
            heading="Network · Nearest IX"
            // Match the governed ~10.2 ms ESTIMATED IX-latency value: 1 dp, not 3
            // (3 dp implied false precision on an estimate).
            heroValue={formatFixed(latencyMs, 1)}
            heroUnit="ms"
            heroLabel="Latency to Nearest IX"
            heroTone={latencyRag === 'green' ? 'positive' : latencyRag === 'amber' ? 'warning' : 'negative'}
            chip={latencyRag ?? undefined}
            // Label the RAG chip with the METRIC it rates ("IX Latency"), not the
            // bare "Amber"/"Red" verdict word — so it never reads as a single
            // verdict against the separate Fiber-Density chip below.
            chipLabel="IX Latency"
            rows={fiberDensity ? [{ key: 'Fiber density', value: fiberDensity }] : undefined}
            caveat="Round-trip latency to the nearest internet exchange; verify against carrier route maps."
          />
        )}
        {/* CONNECTIVITY-ADVISORY — 'Unknown' (no connectivity data) renders no
            RAG chip/hero: an absence is not a tier and must not be colored.
            The plain "Fiber density" row above still shows "Unknown". */}
        {fiberDensity && fiberDensity !== 'Unknown' && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <EditorialHeroMetric
              theme={theme}
              size="sm"
              label="Fiber Density"
              value={fiberDensity}
              tone={fiberDensity === 'High' ? 'positive' : fiberDensity === 'Low' ? 'negative' : 'warning'}
            />
            {/* Label the density RAG chip with its METRIC ("Fiber Density") so it
                is unambiguous against the "IX Latency" chip above (different
                metrics, not one contradictory verdict). */}
            <EditorialStatusChip theme={theme} status={FIBER_RAG[fiberDensity]}>Fiber Density</EditorialStatusChip>
          </div>
        )}
        <EditorialRecommendations
          theme={theme}
          items={((report.connectivityStudy?.recommendations) || []).map((r) => renderCitedText(r))}
        />
        {(report.connectivityStudy?.risks?.length ?? 0) > 0 && (
          <FindingClaimsList
            entries={report.connectivityStudy?.risks ?? []}
            references={report.references}
            siteId={report.siteId}
            reportId={report.id}
            sectionId="connectivityStudy"
            responses={report.openQuestionResponses}
            redFlagsHeading="Connectivity risks (confirmed)"
            className="space-y-4"
          />
        )}
      </div>
    </div>
  );
};
